import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WorkscoreShell from '../../components/workscore/WorkscoreShell';
import { getWorkscoreResult } from '../../features/workscore/api';

function getPerformanceCategory(percentage) {
  if (percentage >= 85) return { label: 'Outstanding', color: '#30d158', description: 'Exceptional behavioral intelligence. Strong hire signal.' };
  if (percentage >= 70) return { label: 'Strong', color: '#0071e3', description: 'Above average performance. Hire with confidence.' };
  if (percentage >= 55) return { label: 'Competent', color: '#ff9f0a', description: 'Meets baseline expectations. Consider for development track.' };
  if (percentage >= 40) return { label: 'Developing', color: '#ff6b2b', description: 'Below average. Significant coaching required.' };
  return { label: 'Not Recommended', color: '#ff375f', description: 'Does not meet minimum threshold for this role.' };
}

function toClock(seconds) {
  const total = Number(seconds || 0);
  const hh = String(Math.floor(total / 3600)).padStart(2, '0');
  const mm = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

export default function WorkscoreResultsPage() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [result, setResult] = useState(null);
  const [animatedPercent, setAnimatedPercent] = useState(0);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      const cached = sessionStorage.getItem(`workscore_result_${slug}`);
      if (cached && mounted) {
        setResult(JSON.parse(cached));
        return;
      }

      const attemptId = sessionStorage.getItem(`workscore_attempt_${slug}`);
      if (!attemptId) return;

      try {
        const payload = await getWorkscoreResult(attemptId);
        if (!mounted) return;
        setResult(payload);
      } catch {
        // non-blocking fallback
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [slug]);

  useEffect(() => {
    if (!result) return;
    const target = Math.max(0, Math.min(100, Number(result.overallPercentage || 0)));
    let current = 0;
    const timer = setInterval(() => {
      current += 1;
      setAnimatedPercent(current);
      if (current >= target) {
        clearInterval(timer);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [result]);

  const category = useMemo(() => getPerformanceCategory(Number(result?.overallPercentage || 0)), [result?.overallPercentage]);
  const overallTotalPoints = useMemo(
    () => (result?.sectionResults || []).reduce((sum, section) => sum + Number(section.totalPoints || 0), 0),
    [result?.sectionResults]
  );

  if (!result) {
    return (
      <WorkscoreShell>
        <div className="min-h-screen flex items-center justify-center">Loading results...</div>
      </WorkscoreShell>
    );
  }

  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const dashoffset = circumference - (Math.max(0, Math.min(100, animatedPercent)) / 100) * circumference;

  return (
    <WorkscoreShell>
      <div className="mx-auto max-w-6xl px-4 py-10 md:py-16">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6 md:p-8">
          <h1 className="text-3xl font-semibold text-center">{result.assessmentTitle}</h1>
          <p className="text-center text-white/65 mt-2">Assessment Performance Summary</p>

          <div className="mt-8 grid gap-8 lg:grid-cols-[300px_1fr] items-start">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-center">
              <svg width="180" height="180" viewBox="0 0 180 180" className="mx-auto">
                <circle cx="90" cy="90" r={radius} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="12" />
                <circle
                  cx="90"
                  cy="90"
                  r={radius}
                  fill="none"
                  stroke={category.color}
                  strokeWidth="12"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashoffset}
                  transform="rotate(-90 90 90)"
                />
                <text x="90" y="96" textAnchor="middle" className="fill-white text-3xl font-bold">{animatedPercent}%</text>
              </svg>
              <p className="mt-3 font-semibold" style={{ color: category.color }}>{category.label}</p>
              <p className="text-xs text-white/70 mt-1">{result.overallScore} / {overallTotalPoints || 0} points</p>
              <p className="text-xs text-white/60 mt-2">{category.description}</p>
            </div>

            <div>
              <h2 className="text-lg font-semibold">Section Breakdown</h2>
              <div className="mt-4 space-y-3">
                {(result.sectionResults || []).map((section) => (
                  <div key={section.sectionNumber} className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="flex justify-between gap-3 text-sm">
                      <span>{section.sectionTitle}</span>
                      <span>{section.earnedPoints}/{section.totalPoints} ({section.percentageScore}%)</span>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-blue-500" style={{ width: `${section.percentageScore}%` }} />
                    </div>
                    <p className="mt-1 text-xs text-white/60">Time: {toClock(section.timeSpentSeconds)}</p>
                  </div>
                ))}
              </div>

              <h2 className="text-lg font-semibold mt-7">Violations</h2>
              {result.violationCount > 0 ? (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b border-white/15 text-white/65">
                        <th className="py-2">Type</th>
                        <th className="py-2">Count</th>
                        <th className="py-2">Last Occurred</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(result.violations || []).map((v) => (
                        <tr key={v.type} className="border-b border-white/10">
                          <td className="py-2">{v.type}</td>
                          <td className="py-2">{v.count}</td>
                          <td className="py-2">{v.lastOccurred ? new Date(v.lastOccurred).toLocaleString() : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-2 text-sm text-emerald-300">No policy violations recorded.</p>
              )}

              <div className="mt-7 text-sm text-white/70">
                <p>Total Time: {toClock(result.totalTimeSeconds)}</p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-xl bg-blue-600 px-5 py-3 font-semibold"
            >
              Download Report
            </button>
            <button
              type="button"
              onClick={() => navigate('/workscore')}
              className="rounded-xl border border-white/20 bg-white/10 px-5 py-3"
            >
              Return to Assessments
            </button>
          </div>
        </div>
      </div>
    </WorkscoreShell>
  );
}
